<template>
  <CollectionBaseLayer v-if="data.blData.blFields" :data="data" />
  <div id="cl-body" class="grid cl_body">
    <CollectionCompare
      v-if="compareProducts.length"
      :products="compareProducts"
      @on-compare-click="onCompareClick"
      @on-compare-clear="onCompareClear"
    />
    <div class="row">
      <div class="col-12 col-tl-3 cl__left">
        <!-- Collection Breadcrumb -->
        <div v-if="!data.blData.blFields" class="cl_breadcrumb"><a href="/">Home</a> / {{ data.title }}</div>

        <!-- Collection Title Mobile -->
        <h2 class="cl_title is--mobile">{{ data.title }}</h2>

        <!-- Collection Filters -->
        <CollectionFilters
          ref="filters"
          :isSecondTurn="isSecondTurn"
          :isFilterDrawerOpen="isFilterDrawerOpen"
          :collectionData="collectionData"
          :filteredProducts="filteredProducts"
          :currentFilter="currentFilter"
          :appliedFilters="appliedFilters"
          :appliedFiltersCount="appliedFiltersCount"
          :priceFilterRange="priceFilterRange"
          :percentageOffFilterRange="percentageOffFilterRange"
          @updateAppliedFilters="updateAppliedFilters"
          @toggleFilterDrawer="toggleFilterDrawer"
          @clearFilter="clearFilter"
        />
      </div>
      <div class="col-12 col-tl-9 cl__right">
        <!-- Collection Type Filters -->
        <CollectionTypeFilters
          :filterOptions="filters && filters['type']"
          :appliedFilters="appliedFilters && appliedFilters['type']"
          @updateAppliedFilters="updateAppliedFilters"
        />

        <div class="cl_title-sorting-wrapper">
          <!-- Filter By Button Mobile -->
          <button
            @click="toggleFilterDrawer(true)"
            class="btn btn--white btn--filter cl__filter-btn cl__filter-btn--mobile"
          >
            <span>Filter by</span>
            <svg class="cl_arrow-next">
              <use xlink:href="#icon-next"></use>
            </svg>
          </button>

          <!-- Desktop Title -->
          <h2 class="cl_title is--desktop">{{ data.title }}</h2>

          <!-- Sort By Button -->
          <div class="cl__sortwrapper">
            <label class="cl__sortLabel is--desktop" for="sortOrder">Sort by</label>
            <div class="cl__sortInner btn btn--white btn--filter">
              <select
                id="sortOrder"
                v-model="sortOrder"
                @change="getSortedProduct($event)"
                class="cl__sortSelect"
                name="sortOrder"
              >
                <option v-for="(text, value) in sortOptions" :key="value" :value="value">
                  {{ text }}
                </option>
              </select>
              <svg class="cl_arrow-next">
                <use xlink:href="#icon-next"></use>
              </svg>
            </div>
          </div>
        </div>

        <!-- Product Count String -->
        <div class="cl_product-count">{{ productCountString }}</div>

        <!-- Applied Filters Module -->
        <CollectionAppliedFilters
          v-if="appliedFiltersCount > 0"
          :filters="appliedFilters"
          @remove-filter="removeFilter"
          @clear-filter="clearFilter"
        />

        <!-- Products Module -->
        <div v-if="!productLoader" class="cl_products">
          <template v-for="product in displayProducts" :key="product.handle">
            <ProductCard
              v-if="product.product_type != 'card'"
              type="TypeA"
              :isSecondTurn="isSecondTurn"
              :loadMoreProducts="loadMoreProducts"
              :product="product"
              :callouts="data.callouts"
              :quickview="true"
              :showCompare="true"
              :compareProducts="compareProducts"
              @on-compare-click="onCompareClick"
            />
            <ProductGridCard v-else :data="product" />
          </template>
        </div>
        <div v-else class="cph__products">
          <svg v-for="index in 12" :key="index" class="cph__product">
            <use xlink:href="#icon-product-loader"></use>
          </svg>
        </div>

        <!-- Load More button -->
        <div ref="bottomWrapper" class="cl_bottom">
          <div class="cl_product-count">{{ productCountString }}</div>
          <button
            v-if="displayProductCount < filteredProducts.length"
            :disabled="loadMoreProducts"
            class="btn btn--white btn--medium"
            @click="loadMore"
          >
            {{ loadMoreProducts ? 'Loading...' : 'Load More' }}
          </button>
          <div></div>
        </div>

        <!-- SEO Wrapper -->
        <div v-if="data.seoTitle && data.seoData" class="cl_seo-wrapper">
          <h1 class="cl_seo-title">{{ data.seoTitle }}</h1>
          <div
            v-for="(copy, index) in data.seoData"
            :key="index"
            :class="['cl_seo-menu', { active: isAccorExpanded(copy.heading_new) }]"
          >
            <div
              @click="toggleAccorExpansion(copy.heading_new)"
              @keydown="(e) => handleKeyDownOnSEOAccorClick(e, copy.heading_new)"
              class="cl_seo-menu-title-wrapper"
              tabindex="0"
              role="button"
              :aria-expanded="isAccorExpanded(copy.heading_new)"
              :aria-controls="`seo-content-${index}`"
              aria-live="polite"
            >
              <div class="cl_seo-menu-title" v-html="copy.heading_new"></div>
              <svg class="cl_seo-arrow">
                <use xlink:href="#icon-prev"></use>
              </svg>
            </div>
            <div
              class="cl_seo-menu-content"
              v-html="copy.description_new"
              :id="`seo-content-${index}`"
              role="region"
              :aria-labelledby="`seo-heading-${index}`"
              :aria-hidden="!isAccorExpanded(copy.heading_new)"
              aria-live="assertive"
              ></div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { computed, toRefs, ref, onMounted, reactive, onUnmounted } from 'vue';
  import { useStore } from 'vuex';
  import CollectionFilters from './components/collection-filters.vue';
  import CollectionAppliedFilters from './components/collection-applied-filters.vue';
  import CollectionTypeFilters from './components/collection-type-filters.vue';
  import CollectionBaseLayer from './components/collection-baselayer.vue';
  import CollectionCompare from './components/collection-compare.vue';
  import ProductCard from '../global/product-card/index.vue';
  import ProductGridCard from '../global/product-grid-card.vue';
  import urlSearchParams from './composables/urlSeachParams';
  import useSorting from './composables/useSorting';
  import isEqual from 'lodash/isEqual';

  const props = defineProps({
    data: {
      type: Object,
      required: true,
    },
  });

  const { data } = toRefs(props);

  const store = useStore();
  const overlay = new window.Overlay();
  const filters = ref(null);
  const isFilterDrawerOpen = ref(false);
  const collectionData = ref(data.value);
  const appliedFilters = ref({});
  const currentFilter = ref('');
  const displayProductCount = ref(12);
  const bottomWrapper = ref(null);
  const loadMoreProducts = ref(false);
  const productLoader = ref(false);
  const expandedAccor = reactive([]);
  const priceFilterRange = [
    { name: 'Under $30', value: { min: 0, max: 29.99 } },
    { name: '$30 - $50', value: { min: 30, max: 49.99 } },
    { name: '$50 - $150', value: { min: 50, max: 149.99 } },
    { name: '$150 & Up', value: { min: 150, max: 50000 } },
  ];
  const percentageOffFilterRange = [
    { name: 'Under 10%', value: { min: 0, max: 10 } },
    { name: '10% - 20%', value: { min: 10, max: 20 } },
    { name: '20% - 30%', value: { min: 20, max: 30 } },
    { name: '30% - 40%', value: { min: 30, max: 40 } },
    { name: '40% - 50%', value: { min: 40, max: 50 } },
    { name: '50% & Up', value: { min: 50, max: 100 } },
  ];
  const compareProducts = ref([]);

  const { params, addSearchParam } = urlSearchParams();

  const { sortOptions, sortOrder, sortOrderDisplayNames } = useSorting({
    searchParams: params,
    addSearchParam,
  });

  onMounted(() => {
    store.dispatch('fetchWishlistItems');
    productCountParam();
    oosProducts();
    addGridCards();
    loaderObserver();
    filterParams();
    refreshYotpo();
    window.addEventListener('refreshWishlist', refreshWishlist);
  });

  onUnmounted(() => {
    window.removeEventListener('refreshWishlist', refreshWishlist);
  });

  const refreshWishlist = () => {
    store.dispatch('fetchWishlistItems');
  };

  const isSecondTurn = computed(() => {
    const { handle } = collectionData.value;
    return handle.toLowerCase().includes('second-turn');
  });

  const evenPage = computed(() => {
    return (displayProductCount.value / 12) % 2;
  });

  const productCountString = computed(() => {
    const selectedCount = displayProducts.value.length;
    const totalProducts = filteredProducts.value.length;

    if (selectedCount && totalProducts) {
      const countString = totalProducts > 12 ? `-${selectedCount}` : `-${totalProducts}`;
      const itemsString = totalProducts > 1 ? 'items' : 'item';

      return `1${countString} of ${totalProducts} ${itemsString}`;
    } else {
      return '';
    }
  });

  const filteredProducts = computed(() => {
    const { products, colors } = collectionData.value;

    if (appliedFiltersCount.value === 0) {
      return products;
    }

    let modfiedProductsData = products.filter((product) => product.product_type != 'card');

    Object.entries(appliedFilters.value).forEach(([key, filterOptions]) => {
      if (filterOptions.length) {
        switch (key) {
          case 'category':
            modfiedProductsData = modfiedProductsData.filter((product) => filterOptions.includes(product.product_type));
            break;

          case 'color':
            const colorData = filterOptions.flatMap(
              (option) => colors.find((color) => color.colorName === option)?.swatchNames.split(',') || []
            );

            modfiedProductsData = modfiedProductsData.filter((product) =>
              product.variants.some(({ available, color }) => available && colorData.includes(color.toLowerCase()))
            );
            break;

          case 'size':
            modfiedProductsData = modfiedProductsData.filter((product) =>
              product.variants.some(({ available, size }) => available && filterOptions.includes(size))
            );
            break;

          case 'type':
            modfiedProductsData = modfiedProductsData.filter((product) =>
              product.tags.some(
                (tag) =>
                  tag.startsWith('[Category:') && filterOptions.includes(tag.split('[Category: ')[1].slice(0, -1))
              )
            );
            break;

          case 'price':
            const priceRange = priceFilterRange.filter((range) => filterOptions.includes(range.name));
            const minPrice = Math.min(...priceRange.map((range) => range.value.min));
            const maxPrice = Math.max(...priceRange.map((range) => range.value.max));

            modfiedProductsData = modfiedProductsData.filter(
              (product) => minPrice <= product.price / 100 && product.price / 100 <= maxPrice
            );
            break;

          // case 'season':
          //   modfiedProductsData = modfiedProductsData.filter(product =>
          //     product.tags.some(tag => tag.toLowerCase().startsWith('season:') && filterOptions.includes(tag.split(':')[1]))
          //   );
          //   break;

          case 'condition':
            modfiedProductsData = modfiedProductsData.filter((product) =>
              product.options.some((option) => option.name === 'Condition' && filterOptions.includes(option.values[0]))
            );
            break;

          case 'discount':
            const percentageOffRange = percentageOffFilterRange.filter((range) => filterOptions.includes(range.name));
            const minPercentageOff = Math.min(...percentageOffRange.map((range) => range.value.min));
            const maxPercentageOff = Math.max(...percentageOffRange.map((range) => range.value.max));
            modfiedProductsData = modfiedProductsData.filter((product) =>
              product.variants.some(({ price, compare_at_price }) => {
                if (compare_at_price > 0) {
                  const percentageOff = ((compare_at_price - price) / compare_at_price) * 100;
                  return minPercentageOff <= percentageOff && percentageOff <= maxPercentageOff;
                }
              })
            );
            break;

          default:
            break;
        }
      }
    });

    return modfiedProductsData;
  });

  const displayProducts = computed(() => {
    if (appliedFiltersCount.value === 0) {
      return filteredProducts.value.slice(0, displayProductCount.value);
    }

    // If color filter is applied then filter out Product Variants
    let filtered = filteredProducts.value;
    const { colors } = collectionData.value;
    Object.entries(appliedFilters.value).forEach(([key, filterOptions]) => {
      if (key === 'color' && filterOptions.length) {
        const colorData = filterOptions.flatMap(
          (option) => colors.find((color) => color.colorName === option)?.swatchNames.split(',') || []
        );

        let data = filteredProducts.value.reduce((products, product) => {
          product = {
            ...product,
            variants: product.variants.filter(
              ({ available, color }) => available && colorData.includes(color.toLowerCase())
            ),
          };
          products.push(product);
          return products;
        }, []);

        filtered = data;
      }
    });
    return filtered.slice(0, displayProductCount.value);
  });

  const appliedFiltersCount = computed(() =>
    Object.values(appliedFilters.value).reduce((count, val) => count + val.length, 0)
  );

  const toggleFilterDrawer = (value) => {
    let bodyStyle = document.body.style;
    if (value) {
      isFilterDrawerOpen.value = true;
      overlay.show();
      bodyStyle.overflow = 'hidden';
    } else {
      isFilterDrawerOpen.value = false;
      overlay.hide();
      bodyStyle.overflow = 'auto';
    }
  };

  overlay.on('cancel', () => {
    toggleFilterDrawer(false);
  });

  const updateAppliedFilters = (filterKey, options) => {
    currentFilter.value = options.length ? filterKey : '';
    appliedFilters.value = {
      ...appliedFilters.value,
      [filterKey]: options.map((name) => name),
    };

    // Updating query params for filters
    addSearchParam(`f-${filterKey}`, options.map((name) => name).join('~'));
  };

  const removeFilter = (filterKey, option) => {
    appliedFilters.value[filterKey] = appliedFilters.value[filterKey].filter((filter) => filter != option);
    if (appliedFilters.value[filterKey].length) {
      currentFilter.value = filterKey;
    } else {
      currentFilter.value = '';
    }

    // Updating query params for filters
    addSearchParam(`f-${filterKey}`, appliedFilters.value[filterKey].map((name) => name).join('~'));
  };

  const clearFilter = () => {
    // Remove query params for filters
    Object.keys(appliedFilters.value).forEach((key) => addSearchParam(`f-${key}`, ''));
    appliedFilters.value = {};
    currentFilter.value = '';
  };

  const addGridCards = () => {
    const { products, inGridCards } = collectionData.value;
    if (inGridCards.length) {
      inGridCards.forEach((inGridCard) => {
        products.splice(inGridCard.gridPosition - 1, 0, {
          ...inGridCard,
          product_type: 'card',
        });
      });
    }
  };

  const oosProducts = () => {
    collectionData.value.products = collectionData.value.products.filter(
      (product) => product.available || product.tags.some((tag) => tag.startsWith('bis'))
    );
  };

  const loaderObserver = () => {
    const options = {
      rootMargin: '100px',
      threshold: 0.25,
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          if (evenPage.value && filteredProducts.value.length > 12) {
            loadMore();
          }
          if (displayProductCount.value === filteredProducts.value.length) {
            observer.unobserve(bottomWrapper.value);
          }
        }
      });
    }, options);

    observer.observe(bottomWrapper.value);
  };

  const filterParams = () => {
    if (!params.value) {
      return;
    }

    // Filters Params
    Object.entries(params.value)
      .filter(([key]) => key.startsWith('f-'))
      .forEach(([key, value]) => {
        const values = value.split('~');
        const filterKey = key.slice(2);

        appliedFilters.value = {
          ...appliedFilters.value,
          [filterKey]: values.map((name) => name),
        };

        if (filterKey === 'price' || filterKey === 'discount') {
          const filterRange = filterKey === 'price' ? priceFilterRange : percentageOffFilterRange;
          addCustomFilterOption(values, filterRange);
        }
      });

    // Sorting Params
    if (Object.entries(params.value).some(([key]) => key.startsWith('sort-order'))) {
      getSortedProduct();
    }
  };

  const addCustomFilterOption = (values, rangeArray) => {
    values.forEach((filterVal) => {
      if (!rangeArray.some((range) => filterVal === range.name)) {
        const data = filterVal.match(/[\d.]+/g);
        rangeArray.push({
          name: filterVal,
          value: { min: data.length === 1 ? 0 : data[0], max: data.length === 1 ? data[0] : data[1] },
        });
      }
    });
  };

  const productCountParam = () => {
    var params = new window.URLSearchParams(window.location.search);
    var countParam = params.get('count');

    if (countParam) {
      displayProductCount.value = countParam;
    } else {
      updateCountParam(12);
    }
  };

  const updateCountParam = (value) => {
    const url = new URL(window.location.href);
    const params = new URLSearchParams(url.search);

    params.set('count', value);

    url.search = params.toString();
    window.history.replaceState({ url: url.toString() }, null, url.toString());
  };

  const loadMore = () => {
    loadMoreProducts.value = true;
    const newCount = displayProductCount.value + 12;

    if (newCount < filteredProducts.value.length) {
      displayProductCount.value = newCount;
    } else {
      displayProductCount.value = filteredProducts.value.length;
    }

    updateCountParam(displayProductCount.value);
    refreshYotpo();

    setTimeout(() => {
      loadMoreProducts.value = false;
    }, 1000);
  };

  const getSortedProduct = async () => {
    productLoader.value = true;
    await fetch(`/collections/${data.value.handle}?view=json&sort_by=${sortOrderDisplayNames[sortOrder.value]}`)
      .then((resp) => resp.json())
      .then((newData) => {
        collectionData.value = newData;
        oosProducts();
        addGridCards();
        refreshYotpo();
        productLoader.value = false;
      });
  };

  const refreshYotpo = () => {
    window.refreshYotpoData();
  };

  const isAccorExpanded = (key) => {
    return expandedAccor.indexOf(key) !== -1;
  };

  const toggleAccorExpansion = (key) => {
    if (isAccorExpanded(key)) {
      expandedAccor.splice(expandedAccor.indexOf(key), 1);
    } else {
      expandedAccor.push(key);
    }
  };

  const handleKeyDownOnSEOAccorClick = (e, heading) => {
    if (e.key === 'Enter' || e.key === ' ') {
      toggleAccorExpansion(heading);
      e.preventDefault();
    }
  };

  const onCompareClick = (checked, product) => {
    if (checked) {
      compareProducts.value = [...compareProducts.value, product];
    } else {
      compareProducts.value = compareProducts.value.filter((ele) => !isEqual(ele.id, product.id));
    }
  };

  const onCompareClear = () => {
    compareProducts.value = [];
  };
</script>
